<template>
  <article class="for-event-organisers-page">
    <AppBanner title="For Event Organisers">
      <Transition
        name="scale-fade-out"
        appear
      >
        <div class="max-w-2xl mx-auto">
          <p class="text-gray-dark font-semibold">
            The Professional Photography Partner for Your Sporting Event
          </p>

          <p class="mb-6">
            Organising any sporting event - big or small - is challenging. From planning routes and venues to managing
            registrations and sponsorships, your plate is more than full. SportPix takes professional event photography
            completely off your shoulders, leaving you to focus entirely on making the day successful and memorable for
            your athletes.
          </p>

          <AppLink :to="{ name: 'app_for_event_organisers', hash: '#contact' } ">
            <AppButton class="btn-solid btn-primary mb-4">
              Contact Us
              <FontAwesomeIcon :icon="faArrowDown" />
            </AppButton>
          </AppLink>
        </div>
      </Transition>
    </AppBanner>

    <AppSection>
      <AppSectionHeading title="Why Partner With SportPix?" />

      <Transition
        name="scale-out"
        appear
      >
        <AppContainer class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-x-16 lg:gap-x-24 !max-w-xxl mx-auto">
          <AppIconBlock
            v-for="(item, index) in iconBlockData"
            :key="index"
            :data="item"
          />
        </AppContainer>
      </Transition>
    </AppSection>

    <AppSection class="bg-secondary-subtle">
      <AppContainer max-width="max-w-3xl">
        <h2 class="mb-10 text-center">
          How the Process Works for Event Organisers
        </h2>

        <AppList :data="listData" />
      </AppContainer>
    </AppSection>

    <div class="section">
      <Transition
        name="fade-right"
        appear
      >
        <AppFeaturedSection
          reverse-order
          :image="{ src: ConfidenceAndTrust, alt: 'image 1' }"
          title="What Does SportPix Cost the Event Organiser?"
          class="pt-0"
        >
          <p>
            In most cases, having SportPix photograph your event doesn't cost you a cent—we earn directly from
            athletes who purchase their photos via our platform. However, certain arrangements, such as event-branded
            sponsorship packages and free-to-athlete images, involve a custom commercial partnership arrangement.
          </p>

          <p>
            We’re always transparent and clear about any pricing and costs, so simply contact us to discuss further
            details - we’ll help find the best solution for your unique event.
          </p>
        </AppFeaturedSection>
      </Transition>

      <Transition
        name="fade-left"
        appear
      >
        <AppFeaturedSection
          :image="{ src: WhatDoesSportpixCost, alt: 'image 2'}"
          title="Confidence and Trust at Every Stage"
        >
          <p>
            We partner thoughtfully and ethically with events across South Africa, from local community races to
            significant national and international events. Every partnership is built on honesty, transparency,
            reliability, and ethical business practices—core values you'll see reflected in everything we do.
          </p>

          <p>
            In addition, SportPix maintains strict privacy and compliance standards around personal images,
            particularly concerning minors. You can confidently assure your event participants that their privacy and
            comfort is fully respected.
          </p>
        </AppFeaturedSection>
      </Transition>
    </div>

    <AppSection
      id="contact"
      class="bg-secondary-subtle"
    >
      <AppContainer
        class="flex flex-col justify-center text-center"
        max-width="max-w-4xl"
      >
        <h2 class="text-primary text-xl md:text-2xl lg:text-3xl font-extrabold mb-4 md:mb-5 duration-100">
          Ready to Partner With SportPix?
        </h2>

        <p>
          Whether you're organising your first local event or a major national race, let's discuss how SportPix can add
          exceptional value to your participants' experience and significantly boost your event's professional profile.
        </p>

        <p class="mb-8">
          Contact our Event Team, and let’s create something extraordinary together.
        </p>

        <IconEmailLink />
      </AppContainer>
    </AppSection>
  </article>
</template>

<script setup>
import {
  AppBanner,
  AppButton,
  AppLink,
  AppContainer,
  AppIconBlock,
  AppSection,
  AppList,
  AppFeaturedSection,
  AppSectionHeading,
} from '@/components/app';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import {
  faCalendarUsers,
  faCameraRetro,
  faShirtRunning,
} from '@fortawesome/pro-light-svg-icons';

import {
  ConfidenceAndTrust,
  WhatDoesSportpixCost,
} from '@/assets/images/views/for-organisers';

import { IconEmailLink } from '@/components/links';

const iconBlockData =
    [
      {
        icon: faCameraRetro,
        header: 'Quality Images That Reflect Your Event\'s True Spirit',
        text: 'Photographs are your best way to showcase your event - today, tomorrow, and into the future. At SportPix, we never settle for average-quality images. Our professional photographers arrive early, scout the best locations, and passionately capture the genuine emotion and excitement of every participant; from the eager smiles at the start line, to the sheer joy at crossing the finish line.',
      },
      {
        icon: faShirtRunning,
        header: 'Instant Access & Engagement for Your Athletes',
        text: 'Athletes love seeing and sharing their memories immediately. SportPix uses advanced bib-recognition technology, allowing us to instantly identify participants\' race numbers, making images available in real time. Your athletes will have online access to their professional-quality photos literally moments after finishing their race - encouraging instant social-media sharing and engagement.',
      },
      {
        icon: faCalendarUsers,
        header: 'Exceptional Event Exposure & Promotion',
        text: 'Your event\'s exposure doesn\'t end when the race finishes. With hundreds or even thousands of participants sharing your event-branded images on social media, your event gains immediate and ongoing organic exposure. We offer customised photo overlays, specifically tailored to showcase official event details, branding, sponsor logos, or key event messages.',
      },
    ];

const listData =
    [
      {
        title: 'Get in Touch',
        content: 'Simply reach out via our [Event Organiser Contact Form] or email us. Our team will get back to you quickly to discuss your event\'s participants, type, location, and specific photography needs and expectations.',
      },

      {
        title: 'Tailor-Made Photography Strategy',
        content: 'We listen carefully to your expectations and goals. Do you want to emphasise the individual athlete experience, showcase scenic landmarks along your route, or prominently feature sponsor branding? Whatever the focus, we\'ll ensure our team understands exactly how you want your event captured and shared.',
      },

      {
        title: 'Event-Day Photography',
        content: 'Our professional team arrives early on event day, thoroughly prepares, scouts the route, and photographs athletes throughout the entire event. Our innovative technology instantly processes images, making them available to athletes almost immediately.',
      },

      {
        title: 'Athlete & event-branded Photo Options',
        content: [
          'Standard athlete purchases: Athletes buy individual images, bulk photo packs, or premium printed merchandise directly via our easy-to-use online store.',
          'Sponsor partnership photos: SportPix can offer your event mutually beneficial commercial partnerships, letting participants access event-branded images free of charge. Sponsors gain broad and immediate exposure through branded images with their logos and messages prominently displayed. (Premium, non-branded images remain available for athletes wishing to purchase them.)',
        ],
        boldWords: [
          'Standard athlete purchases:',
          'Sponsor partnership photos:',
        ],
      },
    ];

</script>
